<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  cardTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['confirm', 'cancel']);
const { t } = useI18n();

const selectedPreset = ref('');
const customReason = ref('');
const isSubmitting = ref(false);

// Opções predefinidas de motivos (padronizadas para análise futura com LLM)
const presetReasons = computed(() => [
  { id: 'price', label: t('PIPELINE.LOST_REASON.PRESETS.PRICE') },
  { id: 'competitor', label: t('PIPELINE.LOST_REASON.PRESETS.COMPETITOR') },
  { id: 'timing', label: t('PIPELINE.LOST_REASON.PRESETS.TIMING') },
  { id: 'no_budget', label: t('PIPELINE.LOST_REASON.PRESETS.NO_BUDGET') },
  { id: 'no_response', label: t('PIPELINE.LOST_REASON.PRESETS.NO_RESPONSE') },
  { id: 'requirements', label: t('PIPELINE.LOST_REASON.PRESETS.REQUIREMENTS') },
  { id: 'other', label: t('PIPELINE.LOST_REASON.PRESETS.OTHER') },
]);

const isFormValid = computed(() => {
  if (!selectedPreset.value) return false;
  if (selectedPreset.value === 'other' && !customReason.value.trim()) {
    return false;
  }
  return true;
});

const resetForm = () => {
  selectedPreset.value = '';
  customReason.value = '';
  isSubmitting.value = false;
};

const handleConfirm = () => {
  if (!isFormValid.value) return;

  isSubmitting.value = true;

  // Se for "outro", usa o texto customizado, senão usa o label do preset
  const reason =
    selectedPreset.value === 'other'
      ? customReason.value.trim()
      : presetReasons.value.find(r => r.id === selectedPreset.value)?.label ||
        '';

  emit('confirm', reason);
  resetForm();
};

const handleCancel = () => {
  emit('cancel');
  resetForm();
};

// Reset form when modal closes
watch(
  () => props.show,
  newValue => {
    if (!newValue) {
      resetForm();
    }
  }
);
</script>

<template>
  <Modal :show="show" @close="handleCancel">
    <div class="p-6 max-w-md">
      <!-- Header -->
      <div class="flex items-center gap-3 mb-6">
        <div
          class="w-10 h-10 rounded-full bg-n-ruby-3 flex items-center justify-center"
        >
          <fluent-icon icon="dismiss-circle" size="20" class="text-n-ruby-11" />
        </div>
        <div>
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.LOST_REASON.MODAL_TITLE') }}
          </h3>
          <p
            v-if="cardTitle"
            class="text-label-small text-n-slate-11 truncate max-w-[280px]"
          >
            {{ cardTitle }}
          </p>
        </div>
      </div>

      <!-- Subtitle -->
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('PIPELINE.LOST_REASON.MODAL_SUBTITLE') }}
      </p>

      <!-- Preset Options -->
      <div class="space-y-2 mb-4">
        <label
          v-for="preset in presetReasons"
          :key="preset.id"
          class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all"
          :class="[
            selectedPreset === preset.id
              ? 'border-n-ruby-9 bg-n-ruby-3'
              : 'border-n-weak hover:border-n-strong',
          ]"
        >
          <input
            v-model="selectedPreset"
            type="radio"
            :value="preset.id"
            class="w-4 h-4 text-n-ruby-9 border-n-weak focus:ring-n-ruby-9"
          />
          <span
            class="text-label font-medium"
            :class="[
              selectedPreset === preset.id
                ? 'text-n-ruby-11'
                : 'text-n-slate-12',
            ]"
          >
            {{ preset.label }}
          </span>
        </label>
      </div>

      <!-- Custom Reason Input (when "other" is selected) -->
      <div v-if="selectedPreset === 'other'" class="mb-4 animate-fade-in">
        <textarea
          v-model="customReason"
          class="field-base w-full resize-none"
          :placeholder="t('PIPELINE.LOST_REASON.OTHER_PLACEHOLDER')"
          rows="3"
          maxlength="500"
        />
        <p class="text-label-small text-n-slate-11 mt-1 text-right">
          {{ `${customReason.length}/500` }}
        </p>
      </div>

      <!-- Actions -->
      <div
        class="flex items-center justify-end gap-3 pt-4 border-t border-n-weak"
      >
        <Button variant="ghost" color="slate" size="sm" @click="handleCancel">
          {{ t('PIPELINE.LOST_REASON.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="ruby"
          size="sm"
          :disabled="!isFormValid || isSubmitting"
          :is-loading="isSubmitting"
          @click="handleConfirm"
        >
          <fluent-icon icon="dismiss-circle" size="14" class="mr-1" />
          {{ t('PIPELINE.LOST_REASON.CONFIRM') }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
